import { useState } from "react";
import { Aviso, Botao, Chip } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import { resumoDeTelas, useAjustarTelas, type MatrizDeTelas, type PessoaComTelas } from "../../nucleo/telas";
import { useAlterarPerfil } from "../../nucleo/usuarios";
import { ConfirmacaoDeTrocaDePerfil } from "./ConfirmacaoDeTrocaDePerfil";

/**
 * O que uma pessoa alcança, tela a tela. Escolher um perfil aplica o conjunto pronto; marcar ou
 * desmarcar uma tela ajusta só esta pessoa, e a tela diz quais foram ajustadas. As duas ações vão
 * direto ao servidor, que é quem confere o lado, a organização e as travas — nada aqui decide
 * acesso, só mostra e pede. A exceção é a troca de perfil de alguém da equipe com ajuste
 * individual: os ajustes continuam, e a troca só vai depois da mesma confirmação da tabela.
 */
export function TelasDaPessoa({
  pessoa,
  matriz,
  nomeDoPerfil,
}: {
  pessoa: PessoaComTelas;
  matriz: MatrizDeTelas;
  /** Como a tela chama o perfil (os fixos do cliente têm rótulo próprio). */
  nomeDoPerfil: (chave: string) => string;
}) {
  const ajustar = useAjustarTelas();
  const trocar = useAlterarPerfil();
  const { usuario } = pessoa;
  const perfilAtual = usuario.papeis[0];
  const ocupado = ajustar.isPending || trocar.isPending || !usuario.ativo;
  const erro = ajustar.error ?? trocar.error;
  const [pendente, setPendente] = useState<{ papel: string; origem: HTMLElement } | null>(null);

  function aplicar(papel: string, origem: HTMLElement) {
    if (trocar.isPending) return;
    trocar.reset();
    if (usuario.interno && papel !== perfilAtual && pessoa.ajustes.length > 0) {
      setPendente({ papel, origem });
      return;
    }
    setPendente(null);
    trocar.mutate({ usuarioId: usuario.id, papel });
  }

  return (
    <div className="mt-4" data-testid="telas-da-pessoa">
      <h3 className="titulo-secao">Permissões de {usuario.nome}</h3>
      <p className="celula-secundaria mb-2">
        Perfil aplicado: <b>{perfilAtual ? nomeDoPerfil(perfilAtual) : "nenhum"}</b> ·{" "}
        {resumoDeTelas(pessoa.ativas.length, matriz.telas.length)} telas
      </p>
      {/* Os botões não ficam desabilitados durante o envio: o foco volta a eles depois da confirmação. */}
      <div className="mb-3 flex flex-wrap gap-2" role="group" aria-label="Aplicar um perfil pronto">
        {matriz.perfis.map((perfil) => (
          <Botao
            key={perfil.chave}
            tamanho="pequeno"
            variante={perfil.chave === perfilAtual ? "solido" : "fantasma"}
            aria-pressed={perfil.chave === perfilAtual}
            disabled={ajustar.isPending || !usuario.ativo}
            onClick={(evento) => aplicar(perfil.chave, evento.currentTarget)}
            data-testid={`aplicar-perfil-${perfil.chave}`}
          >
            {nomeDoPerfil(perfil.chave)}
          </Botao>
        ))}
      </div>
      {pendente ? (
        <ConfirmacaoDeTrocaDePerfil
          pessoa={pessoa}
          papel={pendente.papel}
          origem={pendente.origem}
          matriz={matriz}
          nomeDoPerfil={nomeDoPerfil}
          enviando={trocar.isPending}
          aoCancelar={() => setPendente(null)}
          aoConfirmar={() => {
            trocar.mutate({ usuarioId: usuario.id, papel: pendente.papel });
            setPendente(null);
          }}
        />
      ) : null}
      <fieldset className="grid gap-2 md:grid-cols-2" disabled={ocupado}>
        <legend className="campo__rotulo">Telas que esta pessoa enxerga</legend>
        {matriz.telas.map((tela) => {
          const ligada = pessoa.ativas.includes(tela.chave);
          const ajustada = pessoa.ajustadas.includes(tela.chave);
          const exclusiva = !ligada && !tela.concedivel;
          return (
            <label key={tela.chave} className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={ligada}
                disabled={exclusiva}
                onChange={() =>
                  ajustar.mutate({ usuarioId: usuario.id, telas: { [tela.chave]: !ligada } })
                }
                data-testid={`tela-${tela.chave}`}
              />
              <span>
                <b>{tela.rotulo}</b>{" "}
                {tela.sensivel ? (
                  <Chip estado="atencao" semPonto data-testid={`sensivel-${tela.chave}`}>
                    sensível
                  </Chip>
                ) : null}{" "}
                {ajustada ? (
                  <Chip estado="aguardando" semPonto data-testid={`ajustada-${tela.chave}`}>
                    ajustada para esta pessoa
                  </Chip>
                ) : null}
                <span className="block celula-secundaria">
                  {tela.descricao}
                  {tela.aviso_sensivel ? ` ${tela.aviso_sensivel}` : ""}
                </span>
              </span>
            </label>
          );
        })}
      </fieldset>
      <p className="celula-secundaria mt-2">
        {usuario.interno
          ? "Ao escolher outro perfil, os ajustes individuais desta pessoa continuam."
          : "Ao escolher outro perfil, os ajustes desta pessoa recomeçam do perfil novo."}
      </p>
      {erro ? (
        <Aviso tom="erro" className="mt-3" data-testid="erro-das-telas">
          {mensagemDoConsole(erro)}
        </Aviso>
      ) : null}
    </div>
  );
}
